/**
 * DEV-2431 — Vekâlet Ücreti Hesaplayıcı sonuç tablosu.
 *
 * API'nin döndürdüğü hesabı TBB hesaplama aracındaki tabloya çevirir. Metinler
 * TBB'dekiyle birebir aynıdır:
 *   "ilk 600.000,00 TL için  (300.000,00 TL)  %16  48.000,00 TL"
 * Kısmen dolan dilimde, dilimde kullanılan tutar parantez içinde yazılır. Üst
 * sınırı olmayan son dilim yalnızca tutar o eşiği aştığında gösterilir. Taban ya
 * da tutar sınırı uygulandığında dilim tablosu yerine tek satırlık metin gösterilir.
 */

export type VekaletUcretiKurali = 'nispi' | 'maktu_taban' | 'tutar_siniri';

export interface VekaletUcretiDilimi {
  sira: number;
  alt_sinir: number;
  ust_sinir: number | null;
  genislik: number | null;
  kullanilan_tutar: number;
  oran: number;
  ucret: number;
}

export interface VekaletUcretiSonucu {
  tutar: number;
  islem_turu: string;
  islem_turu_adi: string;
  mahkeme: { kod: string; ad: string } | null;
  tarife_donemi: { id: number; baslangic_tarihi: string };
  nispi_ucret: number;
  maktu_taban: number;
  vekalet_ucreti: number;
  uygulanan_kural: VekaletUcretiKurali;
  dilimler: Array<VekaletUcretiDilimi>;
}

export interface HesapTablosuSatiri {
  aciklama: string;
  kullanilan: string | null;
  oran: string;
  ucret: string;
}

export class VekaletUcretiHesapTablosu {
  static tl(sayi: number): string {
    return Number(sayi).toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  static oran(oran: number): string {
    return '%' + Number(oran).toLocaleString('tr-TR', {maximumFractionDigits: 2});
  }

  static baslik(sonuc: VekaletUcretiSonucu): string {
    const kapsam = sonuc.mahkeme ? sonuc.mahkeme.ad : sonuc.islem_turu_adi;
    return `${kapsam} ${this.tl(sonuc.tutar)} TL tutar için VEKALET ÜCRETİ Hesap Tablosu`;
  }

  static dilimTablosuGosterilsinMi(sonuc: VekaletUcretiSonucu): boolean {
    return sonuc.uygulanan_kural === 'nispi';
  }

  static dilimSatirlari(sonuc: VekaletUcretiSonucu): Array<HesapTablosuSatiri> {
    return sonuc.dilimler
        .filter((dilim) => dilim.ust_sinir !== null || dilim.kullanilan_tutar > 0)
        .map((dilim, index) => {
          const sonDilim = dilim.ust_sinir === null || dilim.genislik === null;
          const kismen = !sonDilim && dilim.kullanilan_tutar > 0 && dilim.kullanilan_tutar < (dilim.genislik as number);
          return {
            aciklama: sonDilim
                ? `${this.tl(dilim.alt_sinir)} TL'den yukarısı için`
                : `${index === 0 ? 'ilk' : 'sonraki'} ${this.tl(dilim.genislik as number)} TL için`,
            kullanilan: sonDilim || kismen ? `${this.tl(dilim.kullanilan_tutar)} TL` : null,
            oran: this.oran(dilim.oran),
            ucret: `${this.tl(dilim.ucret)} TL`,
          };
        });
  }

  static tekSatirMetni(sonuc: VekaletUcretiSonucu): string {
    if (sonuc.uygulanan_kural === 'tutar_siniri') {
      return `${this.tl(sonuc.tutar)} TL tutarında asıl alacak için, asıl alacak tutarında vekalet ücreti`;
    }
    return `${this.tl(sonuc.tutar)} TL tutarında asıl alacak için vekalet ücreti`;
  }

  static toplamEtiketi(sonuc: VekaletUcretiSonucu): string {
    return this.dilimTablosuGosterilsinMi(sonuc) ? `${this.tl(sonuc.tutar)} TL için TOPLAM` : 'TOPLAM :';
  }

  static toplam(sonuc: VekaletUcretiSonucu): string {
    return `${this.tl(sonuc.vekalet_ucreti)} TL`;
  }
}
